<script setup>
/* eslint-disable */
import { ref, reactive, computed, onMounted, getCurrentInstance, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import axios from "axios";
import axiosClient from "@/axios/axios";
import moment from "moment";

import CustomBtn from "@/components/custom-btn";
import { Skeleton } from "vue-loading-skeleton";
import "vue-loading-skeleton/dist/style.css";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import { crossover, bigSize, notStandard } from "@/helpers/helper-svg";
import helperAuction from "@/helpers/helper-auction";
import {
  getFreeACopart, getFreeAIAAI, getLiveBindCopart, getLiveBindIAAI,
  localstorageGet, mapCar, stringToSlug
} from "@/helpers/helper";

const { proxy } = getCurrentInstance();
const { t } = useI18n({ useScope: "global" });
const router = useRouter();

const link = ref("");
const isSubMenu = ref("calculator");
const loader = ref(false);
const analyticsLoading = ref(false);
const loaderCustomClearance = ref(false);
const courseCurrency = ref(0);
const msg = ref("");
const msgF = ref("");
const openFullText = ref(false);
const images = ref([]);
const dealers = ref([]);
const selectedDealer = ref(null);
const user = ref(JSON.parse(localstorageGet('user') || '{}'));
const auction = ref(null);

const car = reactive({
  lotId: "", salvageId: "", vin_code: "", lastFourDigits: "",
  name: "", year: 0, price: 0,
  capacity: "", accCapacity: 0, engType: "",
  saleStatus: "", cert: "", lic: false, renta: false,
  priceWithInsurense: 0,
  locationState: 'NA',
  isToyCrashed: false,
  insurance: false,
  shippingKL: 650, expedition: 680, planCompany: 0,
  certP: 0, evacuation: 0, sellerType: "",
  forAnalytics: {}
});

const shipping = reactive({ price: 0, priceMain: 0, portTitle: "", oldPrice: 0, sizeTax: 0 });
const tax = reactive({ auction_p: 0, customs_clearance: 0 });
const additional = reactive({ insurence: true, one: false, two: false, three: false });
const analytics = reactive({ histories: [], vinCode: "", avgBids: {}, lotDetails: {} });


const displayName = computed(() => car.name || t('calculator.selectVehicle'));
const isSimpleManager = computed(() => user.value?.email === 'simple_manager@liontrans.com');
const currentMsg = computed(() => openFullText.value ? msgF.value : msg.value);
const goodDocs = computed(() => (car.titleCheck || "").toLowerCase().includes('good'));

const specLines = computed(() => [
  { label: car.engType === 'ELECTRIC' ? t('calculator.kwh') : t('calculator.engineCapacity'), key: car.engType === 'ELECTRIC' ? 'accCapacity' : 'capacity', editable: true },
  { label: t('calculator.fuelType'), value: car.engType || '---' },
  { label: t('calculator.saleStatus'), value: car.saleStatus || '---' },
  { label: t('calculator.deliveryPort'), value: 'Rotterdam (EU)' }
]);

const pricingFields = computed(() => [
  { label: t('calculator.yearOfProduction'), obj: car, key: 'year', unit: t('calculator.unitYear'), needCalc : true },
  { label: t('calculator.finalBid'), obj: car, key: 'price', unit: 'USD', needCalc : true },
  { label: t('calculator.auctionFee'), obj: tax, key: 'auction_p', unit: 'USD', needCalc : true },
  { label: t('calculator.delivery', { port: shipping.portTitle || '' }), obj: shipping, key: 'price', unit: 'USD', needCalc : true },
  { label: t('calculator.transportToPoland'), obj: car, key: 'shippingKL', unit: 'USD', needCalc : true },
  { label: t('calculator.customsClearance'), obj: tax, key: 'customs_clearance', unit: 'USD', needCalc : false },
  { label: t('calculator.rotterdamComplex'), obj: car, key: 'expedition', unit: 'EUR', needCalc : true },
  { label: t('calculator.companyServices'), obj: car, key: 'planCompany', unit: 'USD', needCalc : true },
  { label: t('calculator.otherFees'), obj: car, key: 'evacuation', unit: 'USD', needCalc : true },
]);

const sizeOptions = computed(() => [
  { id: 'one', label: t('calculator.sizeCrossover'), price: '+150$', icon: crossover },
  { id: 'two', label: t('calculator.sizeBig'), price: '+200$', icon: bigSize },
  { id: 'three', label: t('calculator.sizeNonStandard'), price: 'X2', icon: notStandard }
]);

const totalPrice = computed(() => {
  const sum = [
    car.price, shipping.price, tax.auction_p, tax.customs_clearance,
    car.certP, car.planCompany, car.priceWithInsurense,
    car.shippingKL, car.expedition, car.evacuation
  ];
  return sum.reduce((a, b) => a + (Number(b) || 0), 0).toFixed(0);
});


const calcShippingSize = () => {
  let taxForSize = 0;
  let basePrice = shipping.priceMain;

  if (additional.one) taxForSize = 150;
  if (additional.two) taxForSize = 200;
  if (additional.three) taxForSize = basePrice;

  shipping.price = basePrice + taxForSize;
};

async function calcAuctionFee(auctionName) {
  const {data} = await axiosClient.post("get-fees", {
    auction: auctionName,
    price: car.price ?? 0,
    state: car.locationState ?? "state",
    isToyCrashed: car.isToyCrashed ?? false,
  });

  tax.auction_p = Number(data.fee || 0);
}

const getCustomClearance = async () => {
  loaderCustomClearance.value = true;
  const p = Number(car.price) || 0;

  if (link.value.includes('copart')) {
    await calcAuctionFee('copart')
  } else {
    await calcAuctionFee('iaai')
  }

  if (additional.insurence) {
    const insBase = (p + tax.auction_p) * 0.02;
    car.priceWithInsurense = insBase >= 200 ? Math.round(insBase) : 200;
  } else {
    car.priceWithInsurense = 0;
  }

  const payments = p + tax.auction_p + 600;
  const duty = payments * 0.10;
  const vat = (payments + duty) * 0.21;
  tax.customs_clearance = (duty + vat).toFixed(2);

  loaderCustomClearance.value = false;
};

const parseLink = async () => {
  if (!link.value) return;
  loader.value = true;

  auction.value = link.value.includes("copart") ? "copart" : "iaai";

  try {
    const { data } = await axiosClient.post("parse", {
      auction: auction.value,
      destination: "poland",
      url: link.value,
    });

    if (data.info && data.info.images) {
      images.value = data.info.images;
    } else if (data.img) {
      images.value = data.img.map(v => v.url);
    }

    const info = data.info;
    if (info) {
      car.lotId = info.lotNumber || "";
      car.vin_code = info.vin ? info.vin.slice(0, 11) : "";
      car.lastFourDigits = info.vin ? info.vin.slice(-6) : "";
      car.name = `${info.year || ''} ${info.make || ''} ${info.model || ''}`.trim();
      car.year = Number(info.year) || 0;
      car.price = Number(info.price) || 0;

      car.capacity = info.engineVolume ? (info.engineVolume / 1000).toFixed(1) : "";
      car.engType = info.fuelType || "";
      car.locationState = info.locationState;
      car.isToyCrashed = info.isToyCrashed;
      car.insurance = info.insurance;
      car.saleStatus = info.saleStatus || info.damageSecondary || "";
      car.cert = info.titleType || info.title || "";
      car.titleCheck = info.titleCheck;

      car.forAnalytics = {
        make: info.make,
        model: info.model,
        year: info.year,
        engine_volume: car.capacity
      };
    }

    if (data.shipping) {
      shipping.price = shipping.priceMain = data.shipping.cost || 0;
      shipping.portTitle = data.shipping.port || "";
    }

    await getCustomClearance();
    await carAnalytics();

  } catch (e) {
    console.error("Parse error:", e);
    proxy?.$notify?.({ type: "error", text: t('calculator.notifyParseError') });
  } finally {
    loader.value = false;
  }
};

const carAnalytics = async () => {
  analyticsLoading.value = true;
  try {
    const auctionType = link.value.includes('copart') ? 'copart' : 'iaai';
    const vinRes = await helperAuction.getVin(car.lotId, car.salvageId, auctionType);
    if (vinRes?.data?.vin) {
      const fullVin = vinRes.data.vin;
      analytics.vinCode = fullVin;
      car.vin_code = fullVin.slice(0, 11);
      car.lastFourDigits = fullVin.slice(-6);

      const [histories, lotDetails, avgBids] = await Promise.all([
        helperAuction.getVinHistories(fullVin),
        helperAuction.getLotDetails(fullVin, car.lotId, auctionType),
        helperAuction.getAvgBids(prepareAnalyticsData())
      ]);
      analytics.histories = histories;
      analytics.lotDetails = lotDetails;
      analytics.avgBids = avgBids;
    }
  } finally { analyticsLoading.value = false; }
};

const prepareAnalyticsData = () => {
  const data = { ...car.forAnalytics };
  if (car.engType !== 'ELECTRIC' && data.engine_volume) {
    const m = String(data.engine_volume).match(/(\d+\.\d+)/);
    if (m) data.engine_volume = m[0];
  } else { delete data.engine_volume; }
  return data;
};

const toggleShipping = (type) => {
  const current = additional[type];
  additional.one = additional.two = additional.three = false;
  additional[type] = !current;
  calcShippingSize();
  getCustomClearance();
};

const copyVin = () => {
  navigator.clipboard.writeText(analytics.vinCode || (car.vin_code + car.lastFourDigits));
  proxy?.$notify?.({ type: "success", text: t('calculator.notifyVinCopied') });
};

const clearCar = () => {
  link.value = ""; images.value = []; car.price = 0;
  analytics.histories = []; analytics.vinCode = "";
};

const openInsurensePdf = () => window.open("pdf/Poland Insurance.pdf", '_blank');

const share = () => {
  const route = router.resolve({ name: 'share-result', query: { name: car.name, vin: car.vin_code, price: car.price, totalPrice: totalPrice.value }});
  navigator.clipboard.writeText(window.location.origin + route.href);
  proxy?.$notify?.({ type: "success", text: t('calculator.notifyLinkCopied') });
};

// watch(() => car.price, getCustomClearance);
watch(() => additional.insurence, getCustomClearance);
watch(selectedDealer, (val) => {
  if (val && shipping.priceMain) {
    const key = stringToSlug(shipping.portTitle || '');
    const extra = val.taxList?.[key] || 0;
    shipping.price = shipping.priceMain + extra;
    getCustomClearance();
  }
});

onMounted(() => {
  axios.get("https://api.nbp.pl/api/exchangerates/rates/a/usd/?format=json")
      .then(res => {
        if (res.data?.rates?.[0]?.mid) {
          courseCurrency.value = Number(res.data.rates[0].mid);
        }
      })
      .catch(err => {
        console.error("Ошибка курса NBP:", err);
        courseCurrency.value = 4.00;
      });

  axios.get("https://back-end.mad-dev.pp.ua/shipping-msg?type=poland").then(({ data }) => {
    msgF.value = data.value;
    msg.value = data.value.split('<br>', 3).join('<br>') + "...";
  });
  if (isSimpleManager.value) axiosClient.get('/dealers').then(res => dealers.value = res.data);
});
</script>

<template>
  <div class="max-w-[1150px] mx-auto p-2 font-sans text-slate-900 lg:scale-[0.95] lg:origin-top transition-all duration-500">

    <div class="flex flex-col lg:flex-row items-center justify-between gap-3 mb-4">
      <div class="flex w-full lg:w-3/4 h-11 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden focus-within:ring-2 ring-blue-100">
        <div class="relative flex-1">
          <input v-model="link" @keyup.enter="parseLink" type="text" :placeholder="$t('calculator.linkPlaceholder')" class="w-full h-full px-4 outline-none text-sm">
          <button v-if="link" @click="clearCar" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-500"><i class="fa fa-times-circle text-lg"></i></button>
        </div>
        <button @click="parseLink" class="h-full px-8 bg-black text-white font-bold uppercase text-[10px] tracking-widest">{{ $t('calculator.scan') }}</button>
      </div>

      <div v-if="isSimpleManager" class="w-full md:w-64">
        <vue-select v-model="selectedDealer" :options="dealers" label="email" class="custom-vs-compact" :placeholder="$t('calculator.dealerPlaceholder')" />
      </div>

    </div>

    <div class="flex gap-1">
      <button @click="isSubMenu = 'calculator'" :class="[isSubMenu === 'calculator' ? 'bg-[#0f172a] text-white' : 'bg-white text-slate-500']" class="px-8 py-2.5 rounded-t-xl font-bold text-xs shadow-sm transition-all">{{ $t('calculator.tabCalculator') }}</button>
      <button @click="isSubMenu = 'analytics'" :class="[isSubMenu === 'analytics' ? 'bg-[#0f172a] text-white' : 'bg-white text-slate-500']" class="px-8 py-2.5 rounded-t-xl font-bold text-xs shadow-sm flex items-center gap-2">
        {{ $t('calculator.tabAnalytics') }}
        <i v-if="analyticsLoading" class="fa fa-spinner animate-spin"></i>
      </button>
    </div>

    <div class="bg-white rounded-tr-xl rounded-b-xl shadow-xl border border-slate-100 overflow-hidden">

      <div v-if="isSubMenu === 'calculator'" class="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-slate-100">

        <div class="lg:w-[35%] p-4 space-y-5 bg-[#f8fafc]/50">
          <div class="p-3 rounded-lg bg-[#fff7ed] border border-[#ffedd5] text-right text-[#ef4444]">
            <p class="font-bold text-[10px] uppercase mb-1 leading-none">{{ $t('calculator.noExcise') }}</p>
            <button @click="openFullText = !openFullText" class="text-[9px] font-black underline">{{ openFullText ? $t('calculator.hide') : $t('calculator.fullText') }}</button>
            <div v-if="openFullText" class="text-[12px] text-slate-600 mt-2 text-left break-all" v-html="msgF"></div>
          </div>

          <div v-if="!loader && images.length" class="grid grid-cols-4 gap-1 h-36">
            <div class="col-span-3 row-span-2 rounded-lg overflow-hidden shadow-sm border border-white"><img :src="images[0]" class="w-full h-full object-cover"></div>
            <div v-for="(img, i) in images.slice(1, 3)" :key="i" class="rounded overflow-hidden bg-slate-200 border border-white"><img :src="img" class="w-full h-full object-cover"></div>
          </div>

          <div class="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
            <h2 class="text-md font-black text-slate-800 mb-3 text-right truncate">{{ displayName }}</h2>
            <div class="space-y-2">
              <div v-for="p in specLines" :key="p.label" class="flex justify-between items-center text-[10px]">
                <span class="text-slate-400 text-xl whitespace-nowrap">{{ p.label }}</span>
                <div class="flex-1 border-b border-dotted mx-2 border-slate-100"></div>
                <div class="font-bold text-slate-700 text-right">
                  <input v-if="p.editable" v-model="car[p.key]" @change="getCustomClearance" class="w-14 text-right bg-blue-50/50 rounded px-1 outline-none">
                  <span v-else>{{ p.value }}</span>
                </div>
              </div>
            </div>

            <div :class="car.titleCheck && car.titleCheck.includes('good') ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'" class="mt-3 p-2.5 rounded-lg border text-[12px] font-medium leading-tight">
              <div v-if="car.titleCheck && car.titleCheck.includes('good')" class="flex justify-between font-bold mb-1">
                <span>{{ $t('calculator.document') }}</span><span class="text-right">{{ $t('calculator.goodForExport') }}</span>
              </div>
              <p v-else>{{ $t('calculator.riskyDocuments') }}</p>
            </div>
            <div
                v-if="car.lotId"
                :class="car.sublot ? 'bg-red-100 text-red-800 border-red-200' : 'bg-green-50 text-green-700 border-green-100'"
                class="mt-2 p-2.5 rounded-lg border text-[10px] font-bold flex justify-between items-center transition-all"
            >
              <div class="flex items-center gap-2">
                <i :class="car.sublot ? 'fa fa-exclamation-triangle text-red-600' : 'fa fa-check-circle text-green-600'"></i>
                <span class="uppercase tracking-wider">{{ $t('calculator.sublotStatus') }}</span>
              </div>
              <span class="uppercase">
                {{ car.sublot ? $t('calculator.sublotWarning') : $t('calculator.sublotStandard') }}
              </span>
              <p v-if="car.sublot" class="text-[8px] text-red-500 mt-1 font-bold italic px-1">
                {{ $t('calculator.sublotNote') }}
              </p>
            </div>
            <div
                v-if="car.lotId"
                :class="car.insurance
      ? 'bg-green-50 text-green-700 border-green-100'
      : 'bg-red-100 text-red-800 border-red-200'"
                class="mt-2 p-3 rounded-md text-xs font-medium flex flex-col gap-1 transition-all"
            >
              <div class="flex items-center gap-2">
                <i :class="car.insurance
        ? 'fa fa-exclamation-circle text-green-700'
        : 'fa fa-store text-red-800'">
                </i>
                <span class="uppercase tracking-wide">
      {{ car.insurance ? $t('calculator.fromInsurer') : $t('calculator.fromDealer') }}
    </span>
              </div>

              <p :class="car.insurance ? 'text-green-700 italic' : 'text-red-800 italic'">
                {{ car.insurance
                  ? $t('calculator.fromInsurerNote')
                  : $t('calculator.fromDealerNote') }}
              </p>
            </div>

          </div>
        </div>

        <div class="lg:w-[65%] p-5 space-y-5">
          <div class="space-y-1">
            <label class="text-[9px] font-black text-slate-400 uppercase block text-right">{{ $t('calculator.vinVerification') }}</label>
            <div class="flex border border-slate-200 rounded-xl overflow-hidden h-10 shadow-sm">
              <div class="bg-slate-50 px-3 flex items-center text-slate-400 font-mono text-[10px] border-r border-slate-200">{{ car.vin_code }}</div>
              <input v-model="car.lastFourDigits" maxlength="6" class="flex-1 px-3 outline-none font-bold text-right text-sm text-slate-700" :placeholder="$t('calculator.sixDigits')">
              <button @click="copyVin" class="px-4 bg-[#0f172a] text-white hover:bg-black transition-colors"><i class="fa fa-copy"></i></button>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <div v-for="field in pricingFields" :key="field.label" class="space-y-1 text-right">
              <label class="text-[12px] font-black text-slate-400 uppercase pr-1">{{ field.label }}</label>
              <div class="relative" v-if="field.needCalc">
                <input type="number" v-model.number="field.obj[field.key]" @change="getCustomClearance" class="w-full p-2 pr-12 rounded-lg border border-slate-200 font-bold text-right outline-none text-sm text-slate-700 focus:border-blue-400">
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 font-bold text-[9px] uppercase">{{ field.unit }}</span>
              </div>
              <div class="relative" v-else>
                <input type="number" v-model.number="field.obj[field.key]" class="w-full p-2 pr-12 rounded-lg border border-slate-200 font-bold text-right outline-none text-sm text-slate-700 focus:border-blue-400">
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 font-bold text-[9px] uppercase">{{ field.unit }}</span>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div class="p-3 bg-slate-50/50 rounded-xl border border-slate-100">
              <div class="grid grid-cols-3 gap-2">
                <button v-for="s in sizeOptions" :key="s.id" @click="toggleShipping(s.id)" :class="[additional[s.id] ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105' : 'bg-white text-slate-400 border-slate-200']" class="flex flex-col items-center py-2 rounded-lg border transition-all">
                  <div v-html="s.icon" class="mb-1 [&>svg]:w-15 [&>svg]:h-5"></div>
                  <span class="text-[14px] font-black uppercase">{{ s.label }}</span>
                </button>
              </div>
            </div>
            <div class="space-y-2">
              <label class="flex items-center justify-end gap-3 p-3 bg-white border border-slate-100 rounded-xl cursor-pointer hover:border-blue-200 transition-all shadow-sm">
                <div class="flex flex-col text-right">
                  <span class="text-[10px] font-black text-slate-700 leading-none mb-1">{{ $t('calculator.insurance') }}</span>
                  <span class="text-[9px] text-blue-500 font-bold">+${{ car.priceWithInsurense }} USD</span>
                </div>
                <input type="checkbox" v-model="additional.insurence" class="w-5 h-5 rounded border-slate-300 accent-blue-600">
              </label>
              <button @click="openInsurensePdf" class="w-full text-right text-[8px] font-bold text-slate-400 uppercase pr-1">{{ $t('calculator.terms') }} <i class="fa fa-file-pdf ml-1"></i></button>
            </div>
          </div>

          <div class="p-5 bg-[#0f172a] rounded-[1.2rem] text-white shadow-xl flex flex-col md:flex-row justify-between items-center gap-4 relative overflow-hidden group">
            <div class="absolute -right-10 -bottom-10 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl group-hover:bg-blue-500/10 transition-all duration-700"></div>
            <div class="text-right md:text-left w-full relative z-10">
              <p class="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-0.5">{{ $t('calculator.totalEstimated') }}</p>
              <div class="text-5xl font-black tabular-nums tracking-tighter">{{ totalPrice }} <span class="text-lg font-light opacity-40 uppercase">usd</span></div>
            </div>
            <div class="text-right w-full relative z-10">
              <div class="text-2xl font-bold"><span class="text-xs opacity-40 font-normal">~</span> {{ (totalPrice * courseCurrency).toLocaleString() }} <span class="text-xs opacity-40 font-normal uppercase">pln</span></div>
              <p class="text-[12px] text-slate-500 italic mt-0.5 font-bold">{{ $t('calculator.priceNote') }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="p-5 space-y-6">
        <div v-if="!analyticsLoading && analytics.avgBids?.data" class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="p-3 bg-slate-50 rounded-xl border border-slate-100 text-center">
            <p class="text-[8px] font-black text-slate-400 uppercase mb-1">{{ $t('calculator.avgPrice') }}</p>
            <p class="text-lg font-black text-slate-800">${{ analytics.avgBids.data.avg || 0 }}</p>
          </div>
          <div class="p-3 bg-red-50 rounded-xl border border-red-100 text-center">
            <p class="text-[8px] font-black text-red-400 uppercase mb-1">{{ $t('calculator.minPrice') }}</p>
            <p class="text-lg font-black text-red-600">${{ analytics.avgBids.data.min || 0 }}</p>
          </div>
          <div class="p-3 bg-green-50 rounded-xl border border-green-100 text-center">
            <p class="text-[8px] font-black text-green-400 uppercase mb-1">{{ $t('calculator.maxPrice') }}</p>
            <p class="text-lg font-black text-green-600">${{ analytics.avgBids.data.max || 0 }}</p>
          </div>
          <div v-if="analytics.lotDetails?.data?.price_future" class="p-3 bg-blue-50 rounded-xl border border-blue-100 text-center">
            <p class="text-[8px] font-black text-blue-400 uppercase mb-1">{{ $t('calculator.reserve') }}</p>
            <p class="text-lg font-black text-blue-600">${{ analytics.lotDetails.data.price_future }}</p>
          </div>
        </div>

        <div v-if="analyticsLoading" class="py-20 text-center"><i class="fa fa-spinner animate-spin text-3xl text-slate-200"></i></div>

        <div v-else class="overflow-x-auto rounded-xl border border-slate-100 shadow-sm">
          <table class="w-full text-left text-[10px]">
            <thead class="bg-slate-50 text-slate-400 uppercase font-black tracking-wider">
            <tr>
              <th class="p-3">{{ $t('calculator.lot') }}</th>
              <th class="p-3 text-center">{{ $t('calculator.photo') }}</th>
              <th class="p-3">{{ $t('calculator.status') }}</th>
              <th class="p-3 text-right">{{ $t('calculator.price') }}</th>
              <th class="p-3 text-right">{{ $t('calculator.date') }}</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
            <tr v-for="item in analytics.histories?.data" :key="item.lot_number" class="hover:bg-blue-50/20 transition-colors">
              <td class="p-3 font-bold text-blue-600">{{ item.lot_number }}</td>
              <td class="p-3 text-center">
                <img :src="item.photo ? JSON.parse(item.photo)[0] : ''" class="w-14 h-9 rounded object-cover mx-auto bg-slate-100 shadow-sm">
              </td>
              <td class="p-3"><span class="px-1.5 py-0.5 bg-slate-100 rounded text-[8px] font-bold text-slate-500 uppercase">{{ item.sale_status }}</span></td>
              <td class="p-3 text-right font-black text-slate-900">${{ item.purchase_price }}</td>
              <td class="p-3 text-right text-slate-400">{{ moment.unix(item.sale_date).format('DD.MM.YYYY') }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<style>
.custom-vs-compact .vs__dropdown-toggle { border-radius: 10px; border: 1px solid #e2e8f0; padding: 2px; background: white; height: 44px; }
.custom-vs-compact .vs__selected { font-size: 11px; font-weight: 600; }
</style>